import { useEffect, useState } from "react";
import { Plus, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";
import { pathFor } from "../../i18n/seoMeta";

// Per-service images are stable across languages.
const IMAGES = [
  "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1532619675605-1ede6c2ed2b0?auto=format&fit=crop&w=900&q=80",
];

// Maps an accordion item's `num` ("01"–"05") to its corresponding service
// detail page id. The "Get more information" button uses this to navigate
// to the dedicated SEO subpage instead of scrolling to the contact section.
const NUM_TO_SERVICE_ID = {
  "01": "service.mentoring",
  "02": "service.agile",
  "03": "service.softSkills",
  "04": "service.okr",
  "05": "service.consulting",
  "06": "service.teamDynamics",
  "07": "service.collaboration",
};

export const Services = () => {
  const { lang, t } = useLanguage();
  const FILTERS = t.services.filters;
  const SERVICES = t.services.items;
  const [activeFilter, setActiveFilter] = useState(FILTERS[0]);
  const [openIdx, setOpenIdx] = useState(0);

  // Reset filter when language switches (filters strings change).
  useEffect(() => {
    setActiveFilter(FILTERS[0]);
  }, [FILTERS]);

  useEffect(() => {
    if (activeFilter === FILTERS[0]) return;
    const idx = SERVICES.findIndex((s) => s.title === activeFilter);
    if (idx !== -1) setOpenIdx(idx);
  }, [activeFilter, FILTERS, SERVICES]);

  // Listen for external `set-service-filter` events (dispatched from the Hero
  // hashtag buttons). The event detail carries the FILTER index (0-based)
  // into `FILTERS`, which is language-aware. We resolve the actual filter
  // label here so re-renders after a language switch still work.
  useEffect(() => {
    const onSetFilter = (e) => {
      const idx = e?.detail?.filterIndex;
      if (typeof idx === "number" && FILTERS[idx] !== undefined) {
        setActiveFilter(FILTERS[idx]);
      }
    };
    window.addEventListener("set-service-filter", onSetFilter);
    return () => window.removeEventListener("set-service-filter", onSetFilter);
  }, [FILTERS]);

  const visibleServices = SERVICES.filter(
    (s) => activeFilter === FILTERS[0] || s.title === activeFilter,
  );

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#AEBABE] text-[#181B24]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-16 md:pt-20 pb-8 md:pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-6">
            <p className="font-heading uppercase tracking-[0.2em] text-[#181B24]/80 text-xl md:text-2xl lg:text-3xl mb-4">
              {t.services.eyebrow}
            </p>
            <h2
              data-testid="services-title"
              className="font-heading uppercase font-bold leading-[0.92] tracking-[0.01em] text-6xl md:text-7xl lg:text-[6.5rem]"
            >
              <span className="gradient-text">{t.services.title}</span>
            </h2>
            <p
              data-testid="services-paragraph"
              className="mt-6 text-[#181B24]/80 text-base md:text-lg leading-relaxed font-body max-w-xl"
            >
              {t.services.paragraph}
            </p>
          </div>

          <div
            data-testid="services-filters"
            className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-3"
          >
            {FILTERS.map((f, i) => {
              const isActive = activeFilter === f;
              return (
                <button
                  key={f}
                  data-testid={`filter-${i}`}
                  onClick={() => setActiveFilter(f)}
                  className={`rounded-xl px-5 py-4 md:py-5 text-base md:text-lg font-heading font-semibold tracking-wide transition-all text-left ${
                    isActive
                      ? "gradient-bg text-[#181B24] shadow-[0_10px_30px_rgba(255,226,26,0.3)]"
                      : "bg-white text-[#181B24] hover:bg-[#181B24] hover:text-white"
                  }`}
                >
                  {f}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pb-16 md:pb-20">
        <div data-testid="services-accordion" className="flex flex-col">
          {visibleServices.map((svc) => {
            const realIdx = SERVICES.indexOf(svc);
            const isOpen = openIdx === realIdx;
            const image = IMAGES[realIdx] || IMAGES[0];
            return (
              <div
                key={svc.num}
                data-testid={`accordion-item-${svc.num}`}
                className={`border-t border-[#181B24]/15 first:border-t-0 transition-colors ${
                  isOpen ? "bg-white/40" : ""
                } rounded-2xl ${isOpen ? "my-2" : ""}`}
              >
                <button
                  data-testid={`accordion-toggle-${svc.num}`}
                  onClick={() => setOpenIdx(isOpen ? -1 : realIdx)}
                  className="w-full flex items-center justify-between py-5 md:py-7 px-2 md:px-4 text-left group"
                >
                  <div className="flex items-baseline gap-5 md:gap-8 min-w-0">
                    <span
                      className={`font-heading text-xl md:text-3xl font-semibold transition-colors shrink-0 ${
                        isOpen ? "gradient-text" : "text-[#181B24]/55"
                      }`}
                    >
                      {svc.num}.
                    </span>
                    <span
                      className={`font-heading text-xl md:text-3xl font-semibold transition-colors truncate ${
                        isOpen ? "text-[#181B24]" : "text-[#181B24]/85"
                      }`}
                    >
                      {svc.title}
                    </span>
                  </div>
                  <span
                    className={`shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all ${
                      isOpen
                        ? "bg-[#FFE21A] text-[#181B24]"
                        : "bg-[#181B24] text-[#FFE21A]"
                    }`}
                  >
                    {isOpen ? (
                      <X size={18} strokeWidth={2.5} />
                    ) : (
                      <Plus size={18} strokeWidth={2.5} />
                    )}
                  </span>
                </button>

                {isOpen && (
                  <div
                    data-testid={`accordion-content-${svc.num}`}
                    className="px-2 md:px-4 pb-8 md:pb-12 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 animate-fade-in-up"
                  >
                    <div className="md:col-span-5">
                      <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden">
                        <img
                          src={image}
                          alt={svc.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3 bg-[#181B24] text-[#FFE21A] font-heading font-bold tracking-widest text-xs px-3 py-1.5 rounded-full">
                          {svc.num}
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-7 flex flex-col">
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-[#181B24] leading-tight">
                        {svc.heading.split(" ").map((word, i, arr) => {
                          if (i >= arr.length - 2) {
                            return (
                              <span key={i} className="gradient-text">
                                {word}{" "}
                              </span>
                            );
                          }
                          return <span key={i}>{word} </span>;
                        })}
                      </h3>
                      <div className="mt-4 space-y-3.5 text-[#181B24]/80 font-body text-[0.95rem] leading-relaxed">
                        {svc.body.map((p, i) => (
                          <p key={i}>{p}</p>
                        ))}
                      </div>

                      {svc.domains && (
                        <div className="mt-6">
                          <p className="font-heading uppercase tracking-[0.18em] text-xs text-[#181B24]/60 mb-3">
                            {t.services.focusDomainsLabel}
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                            {svc.domains.map((d) => (
                              <div
                                key={d.name}
                                className="flex gap-3 items-start"
                                data-testid={`soft-skill-${d.name
                                  .toLowerCase()
                                  .replace(/[^a-z0-9]+/g, "-")}`}
                              >
                                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#FFE21A] shrink-0" />
                                <p className="text-[0.9rem] leading-snug">
                                  <span className="font-heading font-bold text-[#181B24]">
                                    {d.name}
                                  </span>
                                  <span className="text-[#181B24]/75">
                                    {" — "}
                                    {d.desc}
                                  </span>
                                </p>
                              </div>
                            ))}
                          </div>
                          {svc.footer && (
                            <p className="mt-5 text-[#181B24]/75 font-body text-[0.9rem] leading-relaxed border-l-2 border-[#FFE21A] pl-4">
                              {svc.footer}
                            </p>
                          )}
                        </div>
                      )}

                      <Link
                        to={pathFor(
                          NUM_TO_SERVICE_ID[svc.num] ?? "services",
                          lang,
                        )}
                        data-testid={`accordion-more-details-${svc.num}`}
                        className="mt-6 self-start inline-flex items-center gap-2 rounded-full gradient-bg text-[#181B24] font-heading font-bold uppercase tracking-[0.15em] text-xs md:text-sm px-6 py-3 hover-lift"
                      >
                        {t.services.moreDetails}
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
